import { ColumnPinningState, ColumnSizingState, SortingState } from '@tanstack/react-table';

import { useLocalStorage } from '@/hooks/useLocalStorage';

interface StoredState {
  columnSizing?: ColumnSizingState;
  sorting?: SortingState;
  // LAX-10118: JSON-safe column ids. Empty array means nothing was saved.
  columnOrder?: string[];
  // Legacy blobs stored column-def-like objects. Read for migration only.
  activeColumns?: any;
  columnVisibility?: any;
  // LAX-10037: user-chosen column pins persisted alongside sizing/visibility
  // so a reload restores them immediately, ahead of the server round-trip.
  columnPinning?: ColumnPinningState;
}

export const usePersistedGridState = (
    storedStateKey?: string,
    insideItemEditor?: boolean,
    storedStateSubKey?: string,
) => {
    // LAX-10039: mirror legacy `getGridStateKey()` — the browse grid stores its
    // state per category (`<category>-state`) or under `default-state` when no
    // category is selected, so switching categories no longer clobbers each
    // other's persisted column order/widths/sort. Grids that don't need a
    // sub-key (e.g. catalog, editor history) keep the legacy flat key.
    const fullKey = storedStateSubKey
        ? `grid_state_${storedStateKey}_${storedStateSubKey}`
        : `grid_state_${storedStateKey}`;
    const [storedState, setStoredState] = useLocalStorage<StoredState>(fullKey, {});

    // Forward both the value and functional-updater forms so effects can merge
    // safely into the latest committed state without stale-closure races.
    const setFilteredStoredState = (newState: StoredState | ((prev: StoredState) => StoredState)) => {
        if (!insideItemEditor) {
            setStoredState(newState);
        }
    };

    return {
        storedState: insideItemEditor ? {} : storedState,
        setStoredState: setFilteredStoredState,
    };
};
